/**
 * ZUNO — ChatCorrida.js
 * Chat em tempo real entre passageiro e motorista durante a corrida
 * Usa Firestore subcoleção: rides/{rideId}/messages
 */
import React, { useState, useEffect, useRef } from "react";
import {
  collection, addDoc, onSnapshot,
  orderBy, query, serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "../firebase";

const MENSAGENS_RAPIDAS_PASSAGEIRO = [
  "Estou chegando! 🏃",
  "Pode aguardar 2 min?",
  "Onde você está?",
  "Já estou no local 📍",
  "Obrigado! 😊",
];

const MENSAGENS_RAPIDAS_MOTORISTA = [
  "Estou chegando! 🚗",
  "Já estou no local 📍",
  "Aguardando você 👋",
  "Trânsito intenso, 2 min",
  "Ok! 👍",
];

export default function ChatCorrida({ rideId, tipoUsuario, nomeOutro, onClose }) {
  const [mensagens, setMensagens] = useState([]);
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const uid = auth.currentUser?.uid;

  const rapidas = tipoUsuario === "passenger"
    ? MENSAGENS_RAPIDAS_PASSAGEIRO
    : MENSAGENS_RAPIDAS_MOTORISTA;

  // Escuta mensagens em tempo real
  useEffect(() => {
    if (!rideId) return;
    const q = query(
      collection(db, "rides", rideId, "messages"),
      orderBy("criadoEm", "asc")
    );
    const unsub = onSnapshot(q, (snap) => {
      setMensagens(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [rideId]);

  // Auto-scroll
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [mensagens]);

  const enviar = async (msg) => {
    const texto_final = (msg || texto).trim();
    if (!texto_final || enviando) return;
    setEnviando(true);
    setTexto("");
    try {
      await addDoc(collection(db, "rides", rideId, "messages"), {
        texto: texto_final,
        remetenteId: uid,
        remetenteTipo: tipoUsuario,
        criadoEm: serverTimestamp(),
      });
    } catch (e) {
      console.error("Erro ao enviar mensagem:", e);
    }
    setEnviando(false);
    inputRef.current?.focus();
  };

  const formatarHora = (ts) => {
    if (!ts?.seconds) return "";
    return new Date(ts.seconds * 1000).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 999999,
      background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end",
    }}>
      <style>{`
        @keyframes slideUpChat { from{opacity:0;transform:translateY(40px)} to{opacity:1;transform:translateY(0)} }
        .msg-bubble { animation: slideUpChat 0.2s ease; }
      `}</style>

      <div style={{
        width: "100%", maxWidth: 480,
        height: "78vh",
        background: "#0a0e1a",
        borderRadius: "24px 24px 0 0",
        display: "flex", flexDirection: "column",
        animation: "slideUpChat 0.3s cubic-bezier(0.34,1.56,0.64,1)",
      }}>
        {/* Header */}
        <div style={{
          padding: "16px 20px",
          borderBottom: "1px solid rgba(255,255,255,0.07)",
          display: "flex", alignItems: "center", gap: 12, flexShrink: 0,
        }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg,#00f2ff,#7b2cbf)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <span style={{ fontSize: 18 }}>{tipoUsuario === "passenger" ? "🚗" : "🚶"}</span>
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ color: "#fff", fontWeight: 700, fontSize: 15, margin: 0 }}>{nomeOutro || "Conversa"}</p>
            <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 12, margin: 0 }}>
              {tipoUsuario === "passenger" ? "Motorista" : "Passageiro"}
            </p>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: "50%", border: "none", background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.6)", fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
        </div>

        {/* Mensagens */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 16px 8px", display: "flex", flexDirection: "column", gap: 8 }}>
          {mensagens.length === 0 && (
            <div style={{ textAlign: "center", padding: "32px 16px" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>💬</div>
              <p style={{ color: "rgba(255,255,255,0.3)", fontSize: 14, margin: 0 }}>
                Nenhuma mensagem ainda.<br />Diga olá para {nomeOutro?.split(" ")[0] || "o outro"}!
              </p>
            </div>
          )}
          {mensagens.map((m) => {
            const minha = m.remetenteId === uid;
            return (
              <div key={m.id} className="msg-bubble" style={{
                display: "flex",
                justifyContent: minha ? "flex-end" : "flex-start",
              }}>
                <div style={{
                  maxWidth: "78%",
                  padding: "10px 14px",
                  borderRadius: minha ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                  background: minha
                    ? "linear-gradient(135deg,#7c3aed,#a855f7)"
                    : "rgba(255,255,255,0.08)",
                  border: minha ? "none" : "1px solid rgba(255,255,255,0.07)",
                }}>
                  <p style={{ color: "#fff", fontSize: 14, margin: 0, lineHeight: 1.45, wordBreak: "break-word" }}>
                    {m.texto}
                  </p>
                  <p style={{ color: minha ? "rgba(255,255,255,0.5)" : "rgba(255,255,255,0.3)", fontSize: 10, margin: "4px 0 0", textAlign: minha ? "right" : "left" }}>
                    {formatarHora(m.criadoEm)}
                    {minha && " ✓"}
                  </p>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>

        {/* Mensagens rápidas */}
        <div style={{
          padding: "8px 12px",
          overflowX: "auto",
          display: "flex", gap: 8,
          flexShrink: 0,
          borderTop: "1px solid rgba(255,255,255,0.05)",
        }}>
          {rapidas.map((r, i) => (
            <button key={i} onClick={() => enviar(r)} style={{
              flexShrink: 0, padding: "7px 14px", borderRadius: 20,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(255,255,255,0.05)",
              color: "rgba(255,255,255,0.7)", fontSize: 12, fontWeight: 600,
              cursor: "pointer", whiteSpace: "nowrap",
            }}>
              {r}
            </button>
          ))}
        </div>

        {/* Input */}
        <div style={{
          padding: "10px 14px 28px",
          display: "flex", gap: 10, alignItems: "center", flexShrink: 0,
          borderTop: "1px solid rgba(255,255,255,0.07)",
        }}>
          <input
            ref={inputRef}
            type="text"
            placeholder="Digite uma mensagem..."
            value={texto}
            onChange={e => setTexto(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviar(); } }}
            style={{
              flex: 1, padding: "11px 16px", borderRadius: 24,
              border: "1px solid rgba(255,255,255,0.1)",
              background: "rgba(255,255,255,0.06)", color: "#fff",
              fontSize: 14, outline: "none",
            }}
          />
          <button
            onClick={() => enviar()}
            disabled={!texto.trim() || enviando}
            style={{
              width: 42, height: 42, borderRadius: "50%", border: "none",
              background: texto.trim() ? "linear-gradient(135deg,#00f2ff,#7b2cbf)" : "rgba(255,255,255,0.08)",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: texto.trim() ? "pointer" : "default",
              transition: "all 0.2s", flexShrink: 0,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}